import React, { Fragment, useCallback, useMemo } from 'react';
import { useResourceDefinitions, useStore, MenuItemLink, useSidebarState } from 'react-admin';
import { useLocation } from 'react-router-dom';
import FiberManualRecordOutlined from '@mui/icons-material/FiberManualRecordOutlined';
import { List, ListItemButton, ListItemIcon, ListItemText, Collapse } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// Icon imports for intelligent mapping
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ScheduleIcon from '@mui/icons-material/Schedule';
import QuizIcon from '@mui/icons-material/Quiz';
import HelpIcon from '@mui/icons-material/Help';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CategoryIcon from '@mui/icons-material/Category';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LabelIcon from '@mui/icons-material/Label';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import WorkIcon from '@mui/icons-material/Work';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import MapIcon from '@mui/icons-material/Map';
import RouteIcon from '@mui/icons-material/Route';
import CommentIcon from '@mui/icons-material/Comment';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import DiscountIcon from '@mui/icons-material/Discount';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StarIcon from '@mui/icons-material/Star';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TuneIcon from '@mui/icons-material/Tune';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';

// Intelligent icon mapping based on resource name patterns
const getDefaultIcon = (resourceName) => {
  const name = resourceName.toLowerCase();
  
  // User-related
  if (name.includes('user') || name.includes('admin')) return PersonIcon;
if (name.includes('people') || name.includes('referral')) return PeopleIcon;
  
  // Quiz/Test-related
  if (name.includes('quiz') || name.includes('test')) return QuizIcon;
  if (name.includes('question')) return HelpIcon;
  if (name.includes('session')) return HistoryIcon;
  
  // Gaming-related
  if (name.includes('gaming') || name.includes('game')) return SportsEsportsIcon;
  if (name.includes('challenge')) return EmojiEventsIcon;
  if (name.includes('balance')) return AccountBalanceWalletIcon;
  
  // Content-related
  if (name.includes('video')) return OndemandVideoIcon;
  if (name.includes('course')) return MenuBookIcon;
  if (name.includes('category') || name.includes('categories')) return CategoryIcon;
  if (name.includes('creator')) return WorkIcon;
  
  // Sales/Payment-related
  if (name.includes('payment') || name.includes('transaction')) return PaymentIcon;
  if (name.includes('subscription')) return ReceiptIcon;
  if (name.includes('coupon')) return LocalOfferIcon;
  if (name.includes('purchase') || name.includes('order')) return ShoppingBagIcon;
  if (name.includes('discount')) return DiscountIcon;
  if (name.includes('trial')) return CardGiftcardIcon;
  
  // Journey/Learning-related
  if (name.includes('journey')) return MapIcon;
  if (name.includes('step')) return RouteIcon;
  
  // Communication-related
  if (name.includes('comment')) return CommentIcon;
  if (name.includes('notification')) return NotificationsIcon;
  if (name.includes('asking') || name.includes('qa')) return QuestionAnswerIcon;
  
  // Schedule/Time-related
  if (name.includes('timeslot') || name.includes('schedule')) return ScheduleIcon;
  if (name.includes('streaming') || name.includes('live')) return LiveTvIcon;
  
  // Location/World-related
  if (name.includes('country') || name.includes('countries')) return PublicIcon;
  if (name.includes('language')) return LanguageIcon;
  if (name.includes('university') || name.includes('universities')) return BusinessIcon;
  
  // Tags and labels
  if (name.includes('tag')) return LabelIcon;
  if (name.includes('label')) return LabelIcon;
  
  // Reviews
  if (name.includes('review')) return StarIcon;
  
  // Config/Settings
  if (name.includes('config') || name.includes('deployment')) return SettingsIcon;
  if (name.includes('feature')) return FiberNewIcon;
  if (name.includes('release') || name.includes('app')) return PhoneAndroidIcon;
  
  // Misc
  if (name.includes('banner')) return TuneIcon;
  if (name.includes('scholarship')) return CardGiftcardIcon;
  if (name.includes('explanation') || name.includes('information')) return InfoIcon;
  
  // Default fallback
  return FiberManualRecordOutlined;
};

const TomYoMenu = () => {
  const resources = useResourceDefinitions();
  const [expanded, setExpanded] = useStore('menu.expanded', []);
  const [sidebarOpen] = useSidebarState();
  const location = useLocation();

  const { parents, childrenByParent, orphans } = useMemo(() => {
    const parents = [];
    const childrenByParent = {};
    const orphans = [];

    Object.values(resources).forEach(def => {
      const options = def.options || {};
      if (options.menu === false) return;
      if (options.isMenuParent) {
        parents.push(def);
        return;
      }
      if (options.menuParent) {
        if (!childrenByParent[options.menuParent]) {
          childrenByParent[options.menuParent] = [];
        }
        childrenByParent[options.menuParent].push(def);
        return;
      }
      orphans.push(def);
    });

    return { parents, childrenByParent, orphans };
  }, [resources]);

  const toggle = useCallback((name) => {
    setExpanded((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  }, [setExpanded]);

  return (
    <List component="nav">
      {parents.map((p) => {
        const open = expanded.includes(p.name);
        const Icon = p.icon;
        const childPaths = (childrenByParent[p.name] || []).map((c) => `/${c.name}`);
        const parentActive = childPaths.some((path) => location.pathname.startsWith(path));
        return (
          <Fragment key={p.name}>
            <ListItemButton
              onClick={() => toggle(p.name)}
              selected={parentActive}
              sx={{
                ...(sidebarOpen ? {} : { justifyContent: 'center' }),
              }}
            >
              {Icon ? (
                <ListItemIcon sx={{ minWidth: sidebarOpen ? 32 : 0, mr: sidebarOpen ? 2 : 0, justifyContent: 'center' }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
              ) : null}
              {sidebarOpen && (
                <ListItemText primaryTypographyProps={{ fontWeight: 'medium', fontSize: 14 }} primary={p.options?.label || p.name} />
              )}
              {sidebarOpen && (open ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {(childrenByParent[p.name] || [])
                  .filter((c) => c.hasList)
                  .map((c) => {
                    const DefaultIcon = c.icon ? c.icon : getDefaultIcon(c.name);
                    return (
                      <MenuItemLink
                        key={c.name}
                        to={`/${c.name}`}
                        primaryText={c.options?.label || c.name}
                        leftIcon={<DefaultIcon fontSize="small" />}
                        sx={{ 
                          pl: sidebarOpen ? 5 : 1.5, 
                          ml: sidebarOpen ? 1 : 0,
                          borderRadius: 1,
                          '&.RaMenuItemLink-active': {
                            backgroundColor: 'action.selected',
                            borderLeft: sidebarOpen ? '3px solid' : 'none',
                            borderLeftColor: 'primary.main',
                          }
                        }}
                      />
                    );
                  })}
              </List>
            </Collapse>
          </Fragment>
        );
      })}
      {orphans.filter((r) => r.hasList).map((r) => {
        const DefaultIcon = r.icon ? r.icon : getDefaultIcon(r.name);
        return (
          <MenuItemLink
            key={r.name}
            to={`/${r.name}`}
            primaryText={r.options?.label || r.name}
            leftIcon={<DefaultIcon fontSize="small" />}
          />
        );
      })}
    </List>
  );
};

export default TomYoMenu;


